import { api } from '../api/client';

/**
 * Converts a URL-safe Base64 string to a Uint8Array (required for PushManager applicationServerKey)
 */
export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Checks whether Web Push and Service Workers are supported in the current browser/device.
 */
export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/**
 * Extracts base64url keys (p256dh and auth) from a PushSubscription.
 */
function getSubscriptionKeys(sub: PushSubscription): { p256dh: string; auth: string } | null {
  const json = sub.toJSON();
  if (json.keys?.p256dh && json.keys?.auth) {
    return {
      p256dh: json.keys.p256dh,
      auth: json.keys.auth,
    };
  }

  const rawP256dh = sub.getKey ? sub.getKey('p256dh') : null;
  const rawAuth = sub.getKey ? sub.getKey('auth') : null;
  if (!rawP256dh || !rawAuth) return null;

  const toB64Url = (buf: ArrayBuffer) => {
    const bytes = new Uint8Array(buf);
    let str = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      str += String.fromCharCode(bytes[i]);
    }
    return window.btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  };

  return {
    p256dh: toB64Url(rawP256dh),
    auth: toB64Url(rawAuth),
  };
}

/**
 * Subscribes this browser/device to Web Push notifications:
 * 1. Obtains backend VAPID public key
 * 2. Calls ServiceWorkerRegistration.pushManager.subscribe()
 * 3. Registers subscription endpoint & keys with backend
 */
export async function subscribeDeviceToPush(): Promise<boolean> {
  if (!isPushSupported()) return false;
  if (Notification.permission !== 'granted') return false;

  try {
    const registration = await navigator.serviceWorker.ready;
    if (!registration.pushManager) return false;

    // 1. Fetch VAPID public key from backend
    const res = await api.get<{ publicKey: string }>('/notifications/push/public-key');
    if (!res?.publicKey) return false;

    const applicationServerKey = urlBase64ToUint8Array(res.publicKey);

    // 2. Fetch or create subscription
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey as unknown as BufferSource,
      });
    }

    const keys = getSubscriptionKeys(subscription);
    if (!keys) return false;

    // 3. Persist subscription for current user in backend
    await api.post('/notifications/push/subscribe', {
      endpoint: subscription.endpoint,
      keys: {
        p256dh: keys.p256dh,
        auth: keys.auth,
      },
    });

    return true;
  } catch (err) {
    console.warn('Failed to subscribe device to Web Push:', err);
    return false;
  }
}

/**
 * Unsubscribes this browser/device from Web Push notifications:
 * 1. Notifies backend to remove subscription endpoint
 * 2. Unsubscribes from PushManager
 */
export async function unsubscribeDeviceFromPush(): Promise<boolean> {
  if (!isPushSupported()) return false;

  try {
    const registration = await navigator.serviceWorker.ready;
    if (!registration.pushManager) return false;

    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      try {
        await api.delete('/notifications/push/unsubscribe', {
          endpoint: subscription.endpoint,
        });
      } catch (err) {
        console.warn('Backend push unsubscribe error:', err);
      }
      await subscription.unsubscribe();
    }
    return true;
  } catch (err) {
    console.warn('Failed to unsubscribe device from Web Push:', err);
    return false;
  }
}
